import { useMemo } from 'react';

import { Billboard, RoundedBox, Text } from '@react-three/drei';

import type { ColorScheme } from '@/lib/colorSchemes';
import { formatValue } from '@/lib/formatValue';
import { getNormalizedValue, normalizeValue } from '@/lib/normalize';
import { useChartStore } from '@/store/chartStore';
import type { ChartData, HandData, HandSegment } from '@/types/chart';

interface SegmentStackProps {
  segments: HandSegment[];
  chartData: ChartData;
  colorScheme: ColorScheme;
  maxHeight: number;
  barSize: number;
  useLogScale: boolean;
  handData: HandData;
  cellX: number;
  cellZ: number;
  isUndefined: boolean;
  onClick: () => void;
  segmentGap: number;
}

interface HandLabelProps {
  hand: string;
  segments: HandSegment[];
  showValues: boolean;
  isUndefined: boolean;
  chartData: ChartData;
  position: [number, number, number];
  invertColors: boolean;
}

/**
 * Renders the hand label (and optional value) as a Billboard
 * Encapsulates label rendering logic including three display modes
 */
function HandLabel({ hand, segments, showValues, isUndefined, chartData, position, invertColors }: HandLabelProps) {
  const primaryColor = invertColors ? '#222222' : '#ffffff';
  const secondaryColor = invertColors ? '#444444' : '#cccccc';
  const outlineColor = invertColors ? '#ffffff' : '#000000';
  const primaryFontSize = invertColors ? 0.22 : 0.2;
  const secondaryFontSize = invertColors ? 0.16 : 0.15;
  const primaryOutlineWidth = invertColors ? 0.006 : 0.01;
  const secondaryOutlineWidth = invertColors ? 0.005 : 0.008;

  return (
    <Billboard position={position}>
      {showValues && !isUndefined && segments.length === 1 ? (
        /* Show value for single-segment hands */
        <group>
          <Text
            position={[0, 0.1, 0]}
            fontSize={primaryFontSize}
            color={primaryColor}
            anchorX="center"
            anchorY="middle"
            textAlign="center"
            outlineWidth={primaryOutlineWidth}
            outlineColor={outlineColor}
          >
            {hand}
          </Text>
          <Text
            position={[0, -0.1, 0]}
            fontSize={secondaryFontSize}
            color={secondaryColor}
            anchorX="center"
            anchorY="middle"
            textAlign="center"
            outlineWidth={secondaryOutlineWidth}
            outlineColor={outlineColor}
          >
            {formatValue(segments[0].value, chartData.unit, segments[0].label)}
          </Text>
        </group>
      ) : showValues && isUndefined ? (
        /* Show 'Fold' for undefined */
        <group>
          <Text
            position={[0, 0.1, 0]}
            fontSize={primaryFontSize}
            color={primaryColor}
            anchorX="center"
            anchorY="middle"
            textAlign="center"
            outlineWidth={primaryOutlineWidth}
            outlineColor={outlineColor}
          >
            {hand}
          </Text>
          <Text
            position={[0, -0.1, 0]}
            fontSize={secondaryFontSize}
            color={secondaryColor}
            anchorX="center"
            anchorY="middle"
            textAlign="center"
            outlineWidth={secondaryOutlineWidth}
            outlineColor={outlineColor}
          >
            Fold
          </Text>
        </group>
      ) : (
        /* Just show hand name for multi-segment or when showValues is false */
        <Text
          fontSize={primaryFontSize}
          color={primaryColor}
          anchorX="center"
          anchorY="middle"
          textAlign="center"
          outlineWidth={primaryOutlineWidth}
          outlineColor={outlineColor}
        >
          {hand}
        </Text>
      )}
    </Billboard>
  );
}

/**
 * Renders the actual segment bars (single, multi-segment bookcase, or wireframe)
 * Encapsulates all segment rendering logic from the outer HandBar component
 */
function SegmentStack({
  segments,
  chartData,
  colorScheme,
  maxHeight,
  barSize,
  useLogScale,
  handData,
  cellX,
  cellZ,
  isUndefined,
  onClick,
  segmentGap,
}: SegmentStackProps) {
  const { hand } = handData;
  const hiddenCategories = useChartStore((state) => state.hiddenCategories);
  const visibleHands = useChartStore((state) => state.visibleHands);

  // Check if this hand is filtered out by the range filter
  const isHandFiltered = visibleHands !== null && !visibleHands.has(hand);

  // Handle undefined/empty state OR filtered out hands
  if (isUndefined || isHandFiltered) {
    return (
      <RoundedBox
        args={[barSize, 0.15, barSize]}
        position={[cellX, 0.075, cellZ]}
        radius={0.02}
        smoothness={4}
        onClick={(e) => {
          e.stopPropagation();
          onClick();
        }}
        onPointerOver={(e) => {
          e.stopPropagation();
          document.body.style.cursor = 'pointer';
        }}
        onPointerOut={() => {
          document.body.style.cursor = 'auto';
        }}
      >
        <meshStandardMaterial color="#666666" wireframe transparent opacity={0.6} />
      </RoundedBox>
    );
  }

  // Bookcase layout: divide cell width by number of segments
  const segmentCount = Math.max(1, segments.length);
  const segmentWidth = barSize / segmentCount;
  const totalBookcaseWidth = segmentCount * segmentWidth + (segmentCount - 1) * segmentGap;
  const bookcaseStartX = cellX - totalBookcaseWidth / 2 + segmentWidth / 2;

  return (
    <>
      {segments.map((segment, index) => {
        const isHidden = segment.label ? hiddenCategories.has(segment.label) : false;
        const value = getNormalizedValue(chartData, segment.value, useLogScale, segment.label);
        // Zero-value segments (at range minimum) should be invisible but selectable
        const isZeroValue = value === 0;
        // Hidden or zero-value segments get minimal height, visible get normal height
        const height = isHidden || isZeroValue ? 0.1 : Math.max(0.1, value * maxHeight);
        const y = height / 2;
        const segmentX = bookcaseStartX + index * (segmentWidth + segmentGap);
        const color = isZeroValue ? undefined : colorScheme.getColor(handData, value, segment.label);

        return (
          <RoundedBox
            key={`${hand}-segment-${
              // biome-ignore lint/suspicious/noArrayIndexKey: we need a unique key for each segment
              index
            }`}
            args={[segmentWidth - segmentGap, height, barSize]}
            position={[segmentX, y, cellZ]}
            radius={0.02}
            smoothness={4}
            onClick={(e) => {
              e.stopPropagation();
              onClick();
            }}
            onPointerOver={(e) => {
              e.stopPropagation();
              document.body.style.cursor = 'pointer';
            }}
            onPointerOut={() => {
              document.body.style.cursor = 'auto';
            }}
          >
            {isZeroValue && <meshStandardMaterial transparent={true} opacity={0.01} />}
            {!isZeroValue && (
              <meshStandardMaterial
                color={color}
                wireframe={isHidden}
                transparent={isHidden}
                opacity={isHidden ? 0.4 : 1}
              />
            )}
          </RoundedBox>
        );
      })}
    </>
  );
}

interface HandBarProps {
  handData: HandData;
  chartData: ChartData;
  colorScheme: ColorScheme;
  maxHeight: number;
  barSize: number;
  gap: number;
  isSelected: boolean;
  onClick: () => void;
  useLogScale: boolean;
  showValues: boolean;
  segmentLayout: 'bookcase' | 'stacked';
  segmentGap: number;
}

/**
 * Renders a hand in the 13x13 grid
 * Handles positioning and selection halo
 * Delegates segment rendering to SegmentStack and label rendering to HandLabel
 */
export default function HandBar({
  handData,
  chartData,
  colorScheme,
  maxHeight,
  barSize,
  gap,
  isSelected,
  onClick,
  useLogScale,
  showValues,
  segmentGap,
}: HandBarProps) {
  const { hand, row, col, segments } = handData;
  const hiddenCategories = useChartStore((state) => state.hiddenCategories);
  const visibleHands = useChartStore((state) => state.visibleHands);
  const invertLabelColors = useChartStore((state) => state.config.invertLabelColors);

  // Calculate grid cell position (center the grid around origin)
  const gridSize = 13;
  const totalSize = gridSize * barSize + (gridSize - 1) * gap;
  const offsetX = -totalSize / 2 + barSize / 2;
  const offsetZ = -totalSize / 2 + barSize / 2;

  const cellX = offsetX + col * (barSize + gap);
  const cellZ = offsetZ + row * (barSize + gap);

  // Determine if this hand is undefined (empty or missing label)
  const hasNoSegments = segments.length === 0;
  const isUndefined = hasNoSegments || (chartData.categoryMapping !== undefined && !segments[0]?.label);

  // Check if this hand is filtered out by the range filter
  const isHandFiltered = visibleHands !== null && !visibleHands.has(hand);

  // Calculate tallest height for positioning halo and label (only visible, non-zero segments)
  const tallestHeight = useMemo(() => {
    // Filtered or undefined hands get wireframe height
    if (isUndefined || hasNoSegments || isHandFiltered) return 0.1;

    const visibleSegments = segments.filter((seg) => !seg.label || !hiddenCategories.has(seg.label));
    if (visibleSegments.length === 0) return 0.1;

    // Get heights, treating zero-value segments as having minimal height
    const heights = visibleSegments.map((seg) => {
      const value = getNormalizedValue(chartData, seg.value, useLogScale, seg.label);
      return value === 0 ? 0.1 : value * maxHeight;
    });

    return Math.max(0.1, ...heights);
  }, [segments, chartData, useLogScale, maxHeight, isUndefined, hasNoSegments, hiddenCategories, isHandFiltered]);

  const textY = tallestHeight + (showValues ? 0.25 : 0.15);
  const haloY = tallestHeight + 0.01;

  return (
    <group>
      {/* Segment rendering - encapsulated */}
      <SegmentStack
        segments={segments}
        chartData={chartData}
        colorScheme={colorScheme}
        maxHeight={maxHeight}
        barSize={barSize}
        useLogScale={useLogScale}
        handData={handData}
        cellX={cellX}
        cellZ={cellZ}
        isUndefined={isUndefined}
        onClick={onClick}
        segmentGap={segmentGap}
      />

      {/* Graduation marks - horizontal lines on the 4 walls */}
      {chartData.graduations?.map((gradValue) => {
        // Convert raw graduation value to height
        const { min, max } = chartData.valueRange ?? { min: 0, max: 1 };
        const normalizedGrad = normalizeValue(gradValue, min, max);
        const gradHeight = normalizedGrad * maxHeight;

        // Only render if graduation is below the bar top (not at or above)
        if (gradHeight <= 0 || gradHeight >= tallestHeight) return null;

        const lineThickness = 0.01;
        const lineDepth = 0.005;
        const halfBar = barSize / 2;
        const offset = 0.002; // Smidge outside the wall to avoid z-fighting

        return (
          <group key={`grad-${gradValue}`}>
            {/* Front wall (positive Z) */}
            <mesh position={[cellX, gradHeight, cellZ + halfBar + offset]}>
              <boxGeometry args={[barSize, lineThickness, lineDepth]} />
              <meshBasicMaterial color="#ffffff" transparent opacity={0.2} />
            </mesh>
            {/* Back wall (negative Z) */}
            <mesh position={[cellX, gradHeight, cellZ - halfBar - offset]}>
              <boxGeometry args={[barSize, lineThickness, lineDepth]} />
              <meshBasicMaterial color="#ffffff" transparent opacity={0.2} />
            </mesh>
            {/* Left wall (negative X) */}
            <mesh position={[cellX - halfBar - offset, gradHeight, cellZ]}>
              <boxGeometry args={[lineDepth, lineThickness, barSize]} />
              <meshBasicMaterial color="#ffffff" transparent opacity={0.2} />
            </mesh>
            {/* Right wall (positive X) */}
            <mesh position={[cellX + halfBar + offset, gradHeight, cellZ]}>
              <boxGeometry args={[lineDepth, lineThickness, barSize]} />
              <meshBasicMaterial color="#ffffff" transparent opacity={0.2} />
            </mesh>
          </group>
        );
      })}

      {/* Selection halo - positioned above tallest segment */}
      {isSelected && (
        <mesh position={[cellX, haloY, cellZ]} rotation={[-Math.PI / 2, 0, 0]}>
          <ringGeometry args={[barSize * 0.35, barSize * 0.45, 32]} />
          <meshBasicMaterial color="#ffffff" transparent opacity={0.8} />
        </mesh>
      )}

      {/* Label - positioned above tallest segment */}
      <HandLabel
        hand={hand}
        segments={segments}
        showValues={showValues}
        isUndefined={isUndefined}
        chartData={chartData}
        position={[cellX, textY, cellZ]}
        invertColors={invertLabelColors}
      />
    </group>
  );
}
